import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAppStore } from "@/stores/RootStore";
import { Separator } from "@/ui/Separator";
import { Header } from "@/components/Header";
import { AdminSensorType, DeviceModel, SelectOption } from "@/types/types";
import { useFetch } from "@/hooks/useFetch";
import Spinner from "@/ui/Spinner/Spinner";

import ModelsGrid from "./ModelsGrid";

export const Models = () => {
  let { id } = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  const currentSite = useAppStore().currentSite;

  const [loading, setLoading] = useState(true);

  const [{ response, isLoading, error }, doFetchItem] = useFetch(
    `/administration/directories/device_models`,
    true,
  );
  const [models, setModels] = useState<DeviceModel[]>();

  const [
    {
      response: sensorsResponse,
      isLoading: sensorsLoading,
      error: errorLoading,
    },
    doFetchSensors,
  ] = useFetch(`/administration/directories/sensor_types`, true);

  const [sensors, setSensors] = useState<SelectOption[]>();

  useEffect(() => {
    doFetchSensors();
  }, []);

  useEffect(() => {
    if (!sensorsResponse) {
      return;
    }
    const sensorsData = sensorsResponse.data as unknown as AdminSensorType[];
    const sensorsList = sensorsData.map((data) => {
      return {
        value: data.attributes.data_key_name,
        label: data.attributes.long_name,
        group: data.attributes.data_key_name.length > 1 ? "test" : "group",
      };
    });
    setSensors(sensorsList);
  }, [sensorsResponse]);

  useEffect(() => {
    setLoading(true);
    doFetchItem();
  }, []);

  useEffect(() => {
    if (!response) {
      return;
    }
    setModels(response.data as unknown as DeviceModel[]);
    setLoading(false);
  }, [response]);

  function empty() {}

  return (
    <div className="flex flex-col w-full h-full">
      <div className="flex-none flex w-full h-14 px-6 justify-between items-center">
        <div className="flex text-base items-center gap-1">
          <NavLink to={"/"}>
            <span className="font-medium">
              {currentSite?.name ? currentSite?.name : "..."}
            </span>
          </NavLink>
          <span>/</span>
          <NavLink to={"/admin"}>
            <span className="font-medium">{t("admin")}</span>
          </NavLink>
          <span>/</span>
          {location.pathname == "/admin/models/" ||
          location.pathname == "/admin/models" ? (
            <span>{t("models")}</span>
          ) : (
            <NavLink to={"/admin/models"}>
              <span>{t("models")}</span>
            </NavLink>
          )}
          {(id || location.pathname == "/admin/models/new") && <span>/</span>}
          {id && (
            <span>{models?.find((n) => n.id == id)?.attributes.name}</span>
          )}
          {location.pathname == "/admin/models/new" && (
            <span>{t("adding_admin_model")}</span>
          )}
        </div>
      </div>
      <Separator className="flex-none" />
      {id || location.pathname == "/admin/models/new" ? (
        <Outlet context={[models, setModels, sensors]} />
      ) : (
        <div className="flex flex-col w-full h-full mx-auto overflow-hidden">
          <Header
            name={t("models")}
            backName={t("admin")}
            backLink={"/admin"}
          />
          {loading ? (
            <Spinner />
          ) : (
            <ModelsGrid models={models!} onClose={empty} />
          )}
        </div>
      )}
    </div>
  );
};
